import Select from "react-select";
import { Form, Row, Col, Button } from "react-bootstrap";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CloseOutlineIcon } from "@components/icons";
import { ITree } from "../../../../../interfaces";

export default function ParentChildTree({ values, setFieldValue, parentOption, childOption }) {
  const [inputFields, setInputFields] = useState<ITree[]>([]);
  const { t } = useTranslation();

  useEffect(() => {
    const b: ITree[] = values?.trees
      ? values.trees.map((a: ITree) => ({
          id: a.id,
          name: a.name,
          represented_trees: a.represented_trees,
          installations: a.installations,
          has_active_sensors: a.has_active_sensors,
        }))
      : [];
    setInputFields(b);
  }, [values?.trees]);

  useEffect(() => {
    setFieldValue("trees", inputFields);
  }, [inputFields]);

  const handleFormChange = (index, event) => {
    const data = [...inputFields];
    if (event.target && event.target.name === "id") {
      const selectedTreeId = event.target.value;
      // Find the selected tree from parentOption to get the full tree data
      const selectedTree = parentOption.find((tree) => tree.id == selectedTreeId);
      if (selectedTree) {
        data[index]["id"] = selectedTree.id;
        data[index]["name"] = selectedTree.name;
        // Preserve other properties if they exist
        if (!data[index]["represented_trees"]) {
          data[index]["represented_trees"] = [];
        }
        // Preserve both sensor data structures for compatibility
        if (selectedTree.installations !== undefined) {
          data[index]["installations"] = selectedTree.installations || [];
        }
        if (selectedTree.has_active_sensors !== undefined) {
          data[index]["has_active_sensors"] = selectedTree.has_active_sensors;
        }
      }
    } else {
      data[index]["represented_trees"] = event;
    }
    setInputFields(data);
  };

  const addFields = () => {
    const newfield: ITree = {
      id: undefined,
      name: "",
      represented_trees: [],
      installations: [],
      has_active_sensors: false,
    };
    setInputFields([...inputFields, newfield]);
  };

  const handleRemove = (index) => {
    const filtered = inputFields.filter((a, i) => i !== index);
    setInputFields(filtered);
  };
  // Show all existing trees that are either parent trees (with sensors) or already in the area
  // Merge existing area trees with possible parent trees to show all relevant options
  const parentIds = parentOption.map((tree) => {
    return +tree.id;
  });

  // Show only trees that have sensors - these can be parent trees
  const isParentTree = (tree: ITree) => {
    if (!tree.id) return true; // Allow empty/new entries

    // Check for installations first (legacy), then fall back to has_active_sensor
    if (tree.installations !== undefined) {
      return tree.installations && tree.installations.length > 0;
    }
    return tree.has_active_sensors === true;
  };

  return (
    <div>
      <Row>
        <Col lg={5}>
          <h6>{t("tree_with_sensor")}</h6>
        </Col>
        <Col lg={5}>
          <h6>{t("represented_tree")}</h6>
        </Col>
      </Row>
      {inputFields?.map((field, index) => {
        // Only render trees that are parent trees (have installations) or empty entries
        if (!isParentTree(field)) return null;

        const selectedTree = parentOption.find((tree) => tree.id == field.id);
        return (
          <Row className="g-1 mb-2" key={index}>
            <Col lg={5}>
              <div>
                <Form.Select
                  name="id"
                  style={{ height: "38px" }}
                  onChange={(event) => handleFormChange(index, event)}
                  value={field.id || ""}
                  defaultValue={"Choose Tree"}
                >
                  <option value="">Choose Tree</option>
                  {/* Show all possible parent trees */}
                  {parentOption?.map((tree) => (
                    <option value={tree.id} key={tree.id}>
                      {tree.name}
                    </option>
                  ))}

                  {/* Also include existing tree if it's not in parentOption but is currently selected */}
                  {field.id && !parentIds.includes(+field.id) && (
                    <option value={field.id} key={`existing-${field.id}`}>
                      {field.name}
                    </option>
                  )}
                </Form.Select>
              </div>
            </Col>
            <Col lg={6}>
              <div>
                <Select
                  classNamePrefix="react-select"
                  id="tree_set"
                  value={field.represented_trees?.map((representedTree) => ({
                    value: representedTree.id,
                    label: representedTree.name,
                  }))}
                  onChange={(option) => {
                    const opt = option?.map((tree) => ({
                      id: tree.value,
                      name: tree.label,
                      type: "Tree",
                    }));
                    handleFormChange(index, opt);
                  }}
                  options={childOption?.map((tree) => ({
                    value: tree.id,
                    label: tree.name,
                  }))}
                  isMulti
                />
              </div>
            </Col>
            <Col>
              <Button
                variant="outline-primary"
                className="border-0 px-2"
                style={{ height: "38px" }}
                onClick={() => handleRemove(index)}
              >
                <CloseOutlineIcon />
              </Button>
            </Col>
          </Row>
        );
      })}
      <button type="button" className="btn btn-primary btn-sm" onClick={addFields}>
        Add More..
      </button>
    </div>
  );
}
